/**
 * useLotes (M10) — dados da tela de lotes e custo sobre React Query.
 *
 * - `useListaLotes`: GET /lotes; polling ENQUANTO houver lote `montando` ou
 *   `em_execucao` (status do contrato); para quando todos terminam.
 * - `useCriarLote` / `useAprovarLote`: POST /lotes e POST /lotes/{id}/aprovacao;
 *   invalidam a lista ao concluir.
 * - `useEstimativaReleitura` / `useReleitura`: GET/POST /lotes/releitura (ADR-034); a
 *   releitura invalida os lotes e o quadro dos documentos sem visita.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useServices } from '../contexts/ServicesContext';
import { PaginaLotes } from '../services/lotesApi';
import { CriarLoteInput, LOTE_STATUS_EM_ANDAMENTO, LoteListItem } from '../services/lotes-types';

export const LOTES_POLL_MS = 5000;

/** True se algum lote ainda está andando (montando/em_execucao). */
export function temLoteEmAndamento(itens: readonly LoteListItem[]): boolean {
  return itens.some((l) => LOTE_STATUS_EM_ANDAMENTO.has(l.status));
}

export function useListaLotes(): {
  itens: readonly LoteListItem[];
  carregando: boolean;
  erro: boolean;
} {
  const { lotes } = useServices();
  const query = useQuery<PaginaLotes>({
    queryKey: ['lotes'],
    queryFn: () => lotes.listar(),
    refetchInterval: (q) => {
      const dados = q.state.data as PaginaLotes | undefined;
      return dados && temLoteEmAndamento(dados.itens) ? LOTES_POLL_MS : false;
    },
  });
  return { itens: query.data?.itens ?? [], carregando: query.isLoading, erro: query.isError };
}

export function useCriarLote() {
  const { lotes } = useServices();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CriarLoteInput) => lotes.criar(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lotes'] }),
  });
}

export function useAprovarLote() {
  const { lotes } = useServices();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (loteId: string) => lotes.aprovar(loteId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lotes'] }),
  });
}

/** GET /lotes/releitura — só quando a confirmação está aberta (custo e quantidade na hora). */
export function useEstimativaReleitura(aberta: boolean) {
  const { lotes } = useServices();
  return useQuery({
    queryKey: ['lotes', 'releitura'],
    queryFn: () => lotes.estimarReleitura(),
    enabled: aberta,
    staleTime: 0,
  });
}

export function useReleitura() {
  const { lotes } = useServices();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => lotes.releitura(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lotes'] });
      void qc.invalidateQueries({ queryKey: ['painel', 'sem-visita'] });
    },
  });
}
